/** Industry options for the analysis */
export type Industry =
  | 'professional_services'
  | 'financial_services'
  | 'healthcare'
  | 'education'
  | 'manufacturing'
  | 'retail'
  | 'government'
  | 'not_for_profit'
  | 'construction'
  | 'technology'
  | 'legal'
  | 'real_estate'
  | 'energy_utilities'
  | 'transport_logistics'
  | 'agriculture'
  | 'media_entertainment'
  | 'telecommunications'
  | 'mining_resources'
  | 'hospitality_tourism'
  | 'ndis'
  | 'other';

export interface IndustryOption {
  value: Industry;
  label: string;
  description: string;
}

/** User context sent with each analysis request */
export interface UserContext {
  user_role: string;
  company_size: string;
  ai_goals: string;
  company_name: string;
  country: string;
  website_url?: string;
  custom_industry?: string;
}

/**
 * Report scan mode.
 * - ``form_only`` (default, Mode A): no environment scan — report built from the
 *   intake form, website, and industry context only.
 * - ``sharepoint`` (Mode B): opt-in crawl of the user-selected SharePoint sites.
 */
export type ScanMode = 'form_only' | 'sharepoint';

/** A single appendable prompt with a stable id (derived server-side). */
export interface SuggestedQuestion {
  id: string;
  text: string;
}

/** A worked example of a strong free-text intake answer, optionally role-framed. */
export interface SampleIntake {
  role?: string;
  ai_goals: string;
  source_doc?: string;
}

/** Pre-canned prompts + a worked example the user can prefill into their intake */
export interface IndustryQuestions {
  industry: string;
  displayName: string;
  role?: string;
  commonScenarios: SuggestedQuestion[];
  governanceCompliance: SuggestedQuestion[];
  sampleIntake?: SampleIntake | null;
}

/** Lightweight SharePoint site summary for the opt-in site-list builder */
export interface SiteSummary {
  id: string;
  name: string;
  url: string;
}

/** Analysis status from the backend */
export type AnalysisStatus =
  | 'pending'
  | 'scanning'
  | 'scoring'
  | 'generating_ai_content'
  | 'generating_report'
  | 'uploading'
  | 'completed'
  | 'failed';

export type AnalysisStep =
  | 'authenticate'
  | 'scan_sites'
  | 'scan_files'
  | 'scan_content_types'
  | 'calculate_score'
  | 'generate_ai_content'
  | 'generate_report'
  | 'upload_report';

/**
 * POST /api/readiness/analyses response.
 *
 * Generation is synchronous — the request runs the full pipeline and returns the
 * finished report inline (no polling). ``status`` is ``'completed'`` (200, with
 * ``reportBase64``), ``'consent_required'`` (403, Mode B only), or ``'failed'``
 * (502, with ``error``).
 */
export interface StartAnalysisResponse {
  analysisId: string;
  status: string;
  fileName?: string;
  score?: number | null;
  tier?: string | null;
  aiContentUsed?: boolean;
  reportStored?: boolean;
  /** Base64-encoded report — present only when status === 'completed'. */
  reportBase64?: string;
  /** MIME type of reportBase64 (DOCX by default, PDF when rendering is enabled). */
  reportMimeType?: string;
  /** Friendly failure message when status === 'failed'. */
  error?: string;
  /** Admin-consent URL when status === 'consent_required'. */
  consentUrl?: string | null;
}

/** GET /api/discovery/analyses/{id}/status response */
export interface AnalysisStatusResponse {
  analysisId: string;
  status: AnalysisStatus;
  currentStep: AnalysisStep | null;
  stepsCompleted: AnalysisStep[];
  totalSteps: number;
  progressPct: number;
  message: string;
  error: string | null;
  consentUrl: string | null;
  startedAt: string;
  completedAt: string | null;
  score: number | null;
  tier: string | null;
  reportStored: boolean;
  aiContentUsed: boolean;
  reportFilename: string | null;
  sitesAnalysed: number | null;
  librariesAnalysed: number | null;
  filesAnalysed: number | null;
}

/** Stored report metadata from blob listing */
export interface StoredReport {
  analysisId: string;
  fileName: string;
  storedAt: string | null;
  sizeBytes: number;
  aiGoals?: string;
  /** Recall metadata (populated on new reports; empty for legacy ones). */
  summary?: string;
  industry?: string;
  userRole?: string;
  country?: string;
  companyName?: string;
}

/** GET /api/discovery/reports response */
export interface ReportsListResponse {
  tenantId: string;
  reports: StoredReport[];
}

/** POST /api/discovery/analyses/{id}/store response */
export interface StoreReportResponse {
  message: string;
  analysisId: string;
  blobPath: string;
}

/** GET /api/discovery/reports/{id} response */
export interface DownloadUrlResponse {
  analysisId: string;
  downloadUrl: string;
}

/** GET /api/discovery/profile response */
export interface ProfileResponse {
  profile: {
    user_role?: string;
    company_size?: string;
    ai_goals?: string;
    company_name?: string;
    country?: string;
    website_url?: string;
    industry?: string;
    custom_industry?: string;
    updated_at?: string;
  };
}

/** GET /api/discovery/rate-limit response */
export interface RateLimitResponse {
  allowed: boolean;
  remaining: number;
  /** Server-built explanation + CTA when blocked (e.g. cap reached). */
  reason?: string;
  /** Which cap was hit: 'individual' | 'company' | ''. */
  scope?: string;
}

/** Wizard step — simplified to consent/analysis/download (industry selection is on HomePage) */
export type WizardStep = 'analysis' | 'download';

export const COMPANY_SIZE_OPTIONS = [
  { value: '1_20', label: '1-20 employees' },
  { value: '21_50', label: '21-50 employees' },
  { value: '51_200', label: '51-200 employees' },
  { value: '201_500', label: '201-500 employees' },
  { value: '501_1000', label: '501-1,000 employees' },
  { value: '1001_5000', label: '1,001-5,000 employees' },
  { value: '5001_plus', label: '5,001+ employees' },
] as const;

export const USER_ROLE_OPTIONS = [
  { value: 'it_admin', label: 'IT Administrator' },
  { value: 'it_manager', label: 'IT Manager' },
  { value: 'business_owner', label: 'Business Owner / CEO' },
  { value: 'cto', label: 'Chief Technology Officer' },
  { value: 'cio', label: 'Chief Information Officer' },
  { value: 'operations_manager', label: 'Operations Manager' },
  { value: 'hr_manager', label: 'HR Manager' },
  { value: 'finance_manager', label: 'Finance Manager' },
  { value: 'marketing_manager', label: 'Marketing Manager' },
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'other', label: 'Other' },
] as const;

export const INDUSTRY_OPTIONS: IndustryOption[] = [
  {
    value: 'agriculture',
    label: 'Agriculture',
    description: 'Farming, agribusiness, food production',
  },
  {
    value: 'construction',
    label: 'Construction',
    description: 'Building, civil, infrastructure, trades',
  },
  {
    value: 'education',
    label: 'Education',
    description: 'Schools, universities, training providers',
  },
  {
    value: 'energy_utilities',
    label: 'Energy & Utilities',
    description: 'Power, water, gas, renewables',
  },
  {
    value: 'financial_services',
    label: 'Financial Services',
    description: 'Banking, insurance, wealth management',
  },
  {
    value: 'government',
    label: 'Government',
    description: 'Federal, state, local government agencies',
  },
  {
    value: 'healthcare',
    label: 'Healthcare',
    description: 'Hospitals, clinics, aged care, allied health',
  },
  {
    value: 'hospitality_tourism',
    label: 'Hospitality & Tourism',
    description: 'Hotels, restaurants, travel, events',
  },
  {
    value: 'legal',
    label: 'Legal',
    description: 'Law firms, barristers, in-house legal',
  },
  {
    value: 'manufacturing',
    label: 'Manufacturing',
    description: 'Production, engineering, supply chain',
  },
  {
    value: 'media_entertainment',
    label: 'Media & Entertainment',
    description: 'Publishing, broadcasting, creative agencies',
  },
  {
    value: 'mining_resources',
    label: 'Mining & Resources',
    description: 'Mining, oil & gas, exploration',
  },
  {
    value: 'ndis',
    label: 'NDIS & Disability Services',
    description: 'NDIS providers, disability support, community care',
  },
  {
    value: 'not_for_profit',
    label: 'Not-for-Profit',
    description: 'Charities, NGOs, community organisations',
  },
  {
    value: 'professional_services',
    label: 'Professional Services',
    description: 'Consulting, accounting, architecture',
  },
  {
    value: 'real_estate',
    label: 'Real Estate & Property',
    description: 'Agencies, property management, development',
  },
  {
    value: 'retail',
    label: 'Retail',
    description: 'Stores, e-commerce, consumer goods',
  },
  {
    value: 'technology',
    label: 'Technology & IT',
    description: 'Software, IT services, SaaS, MSPs',
  },
  {
    value: 'telecommunications',
    label: 'Telecommunications',
    description: 'Telcos, ISPs, network providers',
  },
  {
    value: 'transport_logistics',
    label: 'Transport & Logistics',
    description: 'Freight, shipping, warehousing, fleet',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Any other industry',
  },
];
